import React, { useState } from "react";
import axios from "axios";
import { Mail, ArrowRight, CheckCircle2, AlertTriangle } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

const INITIAL = {
  first_name: "",
  last_name: "",
  email: "",
  phone: "",
  business_name: "",
  business_type: "",
  monthly_volume: "",
  website: "",
  current_processor: "",
  message: "",
};

export default function ContactPage() {
  const [form, setForm] = useState(INITIAL);
  const [status, setStatus] = useState({ state: "idle", msg: "" });

  const onChange = (e) =>
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setStatus({ state: "loading", msg: "" });
    try {
      await axios.post(`${BACKEND_URL}/api/contact`, form);
      setStatus({
        state: "success",
        msg: "Thanks! Our team will reach out within one business day.",
      });
      setForm(INITIAL);
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        err?.message ||
        "Something went wrong. Please try again.";
      setStatus({
        state: "error",
        msg:
          typeof detail === "string"
            ? detail
            : "Please check your inputs and try again.",
      });
    }
  };

  return (
    <>
      <section className="relative overflow-hidden bg-ink-900 text-white">
        <div className="absolute inset-0 bg-hero-gradient" aria-hidden />
        <div className="container-page relative z-10 py-20 text-center sm:py-24">
          <span className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/5 px-3 py-1 text-xs font-semibold uppercase tracking-[0.22em] text-brand-300">
            Contact Us
          </span>
          <h1 className="mx-auto mt-6 max-w-3xl font-display text-4xl font-bold leading-tight sm:text-5xl">
            Get in Touch
          </h1>
          <p className="mx-auto mt-4 max-w-2xl text-base text-white/75 sm:text-lg">
            Ready to transform your payment processing? Contact us for a free, no-obligation quote customized for your business.
          </p>
        </div>
      </section>

      <section className="section bg-white">
        <div className="container-page grid gap-12 lg:grid-cols-5">
          {/* LEFT: Contact info + What Happens Next */}
          <div className="lg:col-span-2">
            <span className="eyebrow">Contact Information</span>
            <h2 className="mt-3 font-display text-2xl font-bold text-ink-900 sm:text-3xl">
              We're here to help
            </h2>
            <p className="mt-4 text-sm leading-relaxed text-ink-500">
              Reach out with questions about merchant accounts, compliance, or alternative payment methods. A payment specialist will get back to you shortly.
            </p>

            <div className="mt-8">
              <div className="flex items-start gap-4">
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-brand-100 text-brand-700">
                  <Mail className="h-5 w-5" />
                </div>
                <div>
                  <div className="text-xs font-semibold uppercase tracking-widest text-ink-400">
                    Email
                  </div>
                  <a
                    href="mailto:hello@snappay.co"
                    className="mt-1 block text-sm font-medium text-ink-700 hover:text-brand-600"
                  >
                    hello@snappay.co
                  </a>
                </div>
              </div>
            </div>

            <div className="mt-10 rounded-2xl border border-ink-900/5 bg-brand-50 p-6">
              <h3 className="font-display text-lg font-bold text-ink-900">
                What Happens Next?
              </h3>
              <ol className="mt-4 space-y-4">
                <Step n={1}>
                  We'll review your information and reach out within 1 business day.
                </Step>
                <Step n={2}>
                  A payment specialist will discuss your needs and provide a custom quote.
                </Step>
                <Step n={3}>
                  If you're ready, we'll start the approval process and get you set up fast.
                </Step>
              </ol>
            </div>
          </div>

          {/* RIGHT: Form */}
          <form
            onSubmit={onSubmit}
            className="rounded-3xl border border-ink-900/5 bg-white p-7 shadow-card lg:col-span-3 sm:p-10"
          >
            <div>
              <span className="eyebrow">Request a Free Quote</span>
              <h2 className="mt-3 font-display text-2xl font-bold text-ink-900 sm:text-3xl">
                Tell us about your business
              </h2>
              <p className="mt-3 text-sm text-ink-500">
                Fill out the form below and we'll get back to you with a customized proposal.
              </p>
            </div>

            <div className="mt-8 grid gap-5 sm:grid-cols-2">
              <Field label="First Name" required>
                <input
                  name="first_name"
                  required
                  value={form.first_name}
                  onChange={onChange}
                  className="input"
                  placeholder="Jane"
                />
              </Field>
              <Field label="Last Name" required>
                <input
                  name="last_name"
                  required
                  value={form.last_name}
                  onChange={onChange}
                  className="input"
                  placeholder="Doe"
                />
              </Field>
              <Field label="Email" required>
                <input
                  type="email"
                  name="email"
                  required
                  value={form.email}
                  onChange={onChange}
                  className="input"
                  placeholder="jane@company.com"
                />
              </Field>
              <Field label="Phone" required>
                <input
                  name="phone"
                  required
                  value={form.phone}
                  onChange={onChange}
                  className="input"
                  placeholder="+1 ..."
                />
              </Field>
              <Field label="Business Name" required>
                <input
                  name="business_name"
                  required
                  value={form.business_name}
                  onChange={onChange}
                  className="input"
                  placeholder="Acme Inc."
                />
              </Field>
              <Field label="Business Type" required>
                <select
                  name="business_type"
                  required
                  value={form.business_type}
                  onChange={onChange}
                  className="input"
                >
                  <option value="">Select type...</option>
                  <option>Restaurant</option>
                  <option>Retail</option>
                  <option>eCommerce</option>
                  <option>Professional Services</option>
                  <option>High-Risk (Peptides, CBD, etc.)</option>
                  <option>Bar/Nightclub</option>
                  <option>Salon/Spa</option>
                  <option>Gym/Fitness</option>
                  <option>Other</option>
                </select>
              </Field>
              <Field label="Monthly Processing Volume">
                <select
                  name="monthly_volume"
                  value={form.monthly_volume}
                  onChange={onChange}
                  className="input"
                >
                  <option value="">Select range...</option>
                  <option>Under $10,000</option>
                  <option>$10,000 - $25,000</option>
                  <option>$25,000 - $50,000</option>
                  <option>$50,000 - $100,000</option>
                  <option>$100,000 - $250,000</option>
                  <option>$250,000+</option>
                </select>
              </Field>
              <Field label="Website">
                <input
                  name="website"
                  value={form.website}
                  onChange={onChange}
                  className="input"
                  placeholder="https://..."
                />
              </Field>
              <div className="sm:col-span-2">
                <Field label="Current Processor">
                  <input
                    name="current_processor"
                    value={form.current_processor}
                    onChange={onChange}
                    className="input"
                    placeholder="e.g. Stripe, Square, or N/A"
                  />
                </Field>
              </div>
              <div className="sm:col-span-2">
                <Field label="Additional Information">
                  <textarea
                    name="message"
                    value={form.message}
                    onChange={onChange}
                    rows={5}
                    className="input resize-y"
                    placeholder="Tell us about your business, pain points, and goals..."
                  />
                </Field>
              </div>
            </div>

            <div className="mt-7 flex flex-wrap items-center justify-between gap-4">
              <p className="text-xs text-ink-400">
                By submitting this form, you agree to be contacted about our services. We respect your privacy and will never share your information.
              </p>
              <button
                type="submit"
                disabled={status.state === "loading"}
                className="btn-primary"
              >
                {status.state === "loading" ? "Sending..." : "Submit Request"}
                <ArrowRight className="h-4 w-4" />
              </button>
            </div>

            {status.state === "success" && (
              <div className="mt-6 flex items-start gap-3 rounded-xl border border-brand-200 bg-brand-50 p-4 text-sm text-brand-800">
                <CheckCircle2 className="mt-0.5 h-5 w-5 text-brand-600" />
                <span>{status.msg}</span>
              </div>
            )}
            {status.state === "error" && (
              <div className="mt-6 flex items-start gap-3 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-800">
                <AlertTriangle className="mt-0.5 h-5 w-5 text-red-500" />
                <span>{status.msg}</span>
              </div>
            )}
          </form>
        </div>
      </section>
    </>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="label">
        {label}
        {required && <span className="text-brand-600"> *</span>}
      </span>
      {children}
    </label>
  );
}

function Step({ n, children }) {
  return (
    <li className="flex items-start gap-3">
      <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-brand-600 text-xs font-bold text-white">
        {n}
      </span>
      <span className="text-sm leading-relaxed text-ink-700">{children}</span>
    </li>
  );
}
